import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import Page from 'components/Page';
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DescriptionListItem from "components/DescriptionListItem";
import {Spotlight, SpotlightData} from "types/Spotlight";
import Button from "components/Button";
import {CheckCircleIcon, PencilIcon, TrashIcon, XCircleIcon} from "@heroicons/react/solid";
import DeleteSpotlight from '../Delete';
import {formattedDateTime} from "services/date";
import {invalidate} from "services/query-client";
import {useNotifications} from "../../../context/NotificationContext";
import BooleanIndicator from "components/BooleanIndicator";
import Chip from "components/Chip";
import SpotlightStatus from "../../../types/SpotlightStatus";
import TernaryIndicator from "../../../components/TernaryIndicator";
import IconBadge from "../../../components/IconBadge";
import IconName from "../../../types/IconName";
import {toTitleCase} from "../../../services/string";
import {singular} from "pluralize";

// Helpers ----

function contentChipName(spotlight: Spotlight): string {
  if(!spotlight.url) return 'Unknown';

  const [scheme, path] = spotlight.url.split(':');

  if (scheme.startsWith('mailto')) return `Email (${path})`;

  if (scheme.startsWith('tel')) return `Call (${path})`;

  if (scheme.startsWith('http')) return `URL (${path.substring(2)})`;

  if (scheme.startsWith('ngage')) {
    const parts = path.substring(2).split('/');

    if (parts.length > 1) {
      return `App Content (${toTitleCase(singular(parts[0]))})`
    }

    return `App Content (${toTitleCase(parts[0])})`
  }

  return 'Unknown';
}

function contentChipHref(spotlight: Spotlight): string {
  if(!spotlight.url) return 'Unknown';

  if(!spotlight.url.startsWith('ngage')) return spotlight.url;

  const path = spotlight.url.split('://')[1]

  return '/' + path.split('/')[0];
}

const statusMap: Record<SpotlightStatus, boolean | null> = {
  [SpotlightStatus.ENABLED]: true,
  [SpotlightStatus.PARTIAL]: null,
  [SpotlightStatus.DISABLED]: false,
}

const statusLabelMap: Record<SpotlightStatus, string> = {
  [SpotlightStatus.ENABLED]: 'Enabled',
  [SpotlightStatus.PARTIAL]: 'Partially Enabled',
  [SpotlightStatus.DISABLED]: 'Disabled',
}

// Component ----

function ViewSpotlight() {
  const {notify} = useNotifications();
  const navigate = useNavigate();

  const {id} = useParams();
  const {data: spotlight, isLoading} = useQuery(
    ['spotlights', {id}],
    () => ngage.spotlights.get(parseInt(id as string))
  )

  const update = useMutation((data: SpotlightData) => ngage.spotlights.update(Number(id), data));

  const [deletingSpotlight, setDeletingSpotlight] = useState<Spotlight | null>(null);

  // Prepare action handlers
  function handleDeleteSpotlight() {
    if (spotlight) setDeletingSpotlight(spotlight)
  }

  function handleEditSpotlight() {
    if (id) navigate(`/spotlights/${id}/edit`)
  }

  async function handleToggleSpotlight() {
    const enabling = !spotlight?.is_enabled

    if(enabling) {
      void handleEnableSpotlight()
    } else {
      void handleDisableSpotlight()
    }
  }

  async function handleEnableSpotlight() {
    if (!spotlight) {
      return
    }

    try {
      await update.mutateAsync({is_enabled: true})
      await invalidate(['spotlights', {id}])

      notify("success", "Spotlight Enabled", `${spotlight.name} has been enabled and will now be displayed`)
    } catch (error) {
      notify("error", "Spotlight Not Updated", `${spotlight.name} enablement could not be changed`)
    }
  }

  async function handleDisableSpotlight() {
    if (!spotlight) {
      return
    }

    try {
      await update.mutateAsync({is_enabled: false})
      await invalidate(['spotlights', {id}])

      notify("success", "Spotlight Disabled", `${spotlight.name} has been disabled and will not be displayed`)
    } catch (error) {
      notify("error", "Spotlight Not Updated", `${spotlight.name} enablement could not be changed`)
    }
  }

  // Prepare breadcrumbs
  const breadcrumbs = {
    "Content": "/",
    "Spotlights": "/spotlights",
    "Spotlight": `/spotlights/${id}`
  }

  // Prepare actions
  const actions = <>
    <Button
      color={Button.colors.WHITE}
      icon={TrashIcon}
      onClick={handleDeleteSpotlight}
      disabled={!spotlight}
    >
      Delete
    </Button>

    <Button
      color={Button.colors.PRIMARY}
      icon={PencilIcon}
      onClick={handleEditSpotlight}
      disabled={!spotlight}
    >
      Edit
    </Button>

    <Button
      color={Button.colors.PRIMARY}
      icon={spotlight?.is_enabled ? XCircleIcon : CheckCircleIcon}
      onClick={handleToggleSpotlight}
      showSpinner={update.isLoading}
    >
      {spotlight?.is_enabled ? 'Disable' : 'Enable'}
    </Button>

    {spotlight?.status === SpotlightStatus.PARTIAL &&
      <Button
        color={Button.colors.PRIMARY}
        icon={CheckCircleIcon}
        onClick={handleEnableSpotlight}
        showSpinner={update.isLoading}
      >
        Enable
      </Button>
    }
  </>

  // Determine which image to show
  const image = spotlight?.image || (spotlight?.content && 'image' in spotlight.content ? spotlight.content.image : null)

  return (
    <Page title={spotlight?.title || "View Spotlight"} breadcrumbs={breadcrumbs} actions={actions}
          isLoading={isLoading}>
      {spotlight &&
        <>
          {/* Spotlight Information */}
          <section className="flex flex-col lg:flex-row-reverse lg:justify-end">
            {!!image &&
              <div
                className="flex justify-center items-center max-h-36 lg:max-h-full shadow overflow-hidden sm:rounded-md sm:mb-2 lg:mb-0 lg:ml-4">
                <img
                  src={image.preview_url}
                  alt={spotlight.title}
                  className="object-cover w-full lg:h-full lg:w-auto"
                />
              </div>
            }

            <div className="lg:basis-2/3 lg:flex-shrink-0 lg:flex-grow">
              <Card
                heading="Spotlight Details"
                subheading={`Created ${formattedDateTime(spotlight.created_at)}`}
              >
                <DescriptionList>
                  <DescriptionListItem span={2}>
                    <DescriptionTerm>Title</DescriptionTerm>
                    <DescriptionDetails>
                      {spotlight.title}
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={2}>
                    <DescriptionTerm>Text</DescriptionTerm>
                    <DescriptionDetails>
                      {spotlight.text ?? 'N/A'}
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Status</DescriptionTerm>
                    <DescriptionDetails>
                      <TernaryIndicator value={statusMap[spotlight.status]} />
                      <span>{' '}{statusLabelMap[spotlight.status]}</span>
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={2}>
                    <DescriptionTerm>Sites</DescriptionTerm>
                    <DescriptionDetails>
                      {spotlight.sites.length < 1 &&
                        <span>All</span>
                      }

                      {spotlight.sites.map(site => (
                        <IconBadge
                          key={site.id}
                          color={IconBadge.colors.BRAND}
                          size={IconBadge.sizes.LARGE}
                          icon={IconName.OFFICE_BUILDING}
                          text={site.name}
                        />
                      ))}
                    </DescriptionDetails>
                  </DescriptionListItem>

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Scheduled</DescriptionTerm>
                    <DescriptionDetails>
                      <BooleanIndicator value={spotlight.is_scheduled}/>
                    </DescriptionDetails>
                  </DescriptionListItem>

                  {spotlight.is_scheduled && spotlight.is_enabled &&
                    <DescriptionListItem span={1}>
                      <DescriptionTerm>Enabled Until</DescriptionTerm>
                      <DescriptionDetails>
                        {spotlight.disable_at ? formattedDateTime(spotlight.disable_at) : 'Forever'}
                      </DescriptionDetails>
                    </DescriptionListItem>
                  }

                  {spotlight.is_scheduled && !spotlight.is_enabled &&
                    <DescriptionListItem span={1}>
                      <DescriptionTerm>Next Enabled At</DescriptionTerm>
                      <DescriptionDetails>
                        {spotlight.is_scheduled ? formattedDateTime(spotlight.enable_at) : 'Never'}
                      </DescriptionDetails>
                    </DescriptionListItem>
                  }

                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Links To</DescriptionTerm>
                    <DescriptionDetails>
                      <Chip name={contentChipName(spotlight)} href={contentChipHref(spotlight)}/>
                    </DescriptionDetails>
                  </DescriptionListItem>

                </DescriptionList>
              </Card>
            </div>
          </section>
        </>
      }

      {/* Delete confirmation modal */}
      <DeleteSpotlight
        show={spotlight === deletingSpotlight}
        spotlight={deletingSpotlight}
        onCancel={() => setDeletingSpotlight(null)}
        onDelete={() => navigate('/spotlights')}
        onDeleteFail={() => setDeletingSpotlight(null)}
      />
    </Page>
  );
}

export default ViewSpotlight;